import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PeepingLogo } from "@/components/PeepingLogo";
import { homeFor } from "@/lib/api";

export default function Login() {
  const { login, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // Login may already be mounted (route guard redirect after logout) when the ?email= switch link arrives.
  const prefill = params.get("email");
  useEffect(() => { if (prefill) setEmail(prefill); }, [prefill]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email.trim(), password);
      navigate(homeFor(user), { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-screen" className="relative min-h-screen bg-[#080808] grain">
      <div className="absolute -top-16 -left-10 h-56 w-56 glow-magenta opacity-40" />
      <div className="mx-auto flex min-h-screen w-full max-w-md flex-col px-6 pb-10 pt-8">
        <Link to="/welcome" data-testid="login-back" className="text-[#a7a0af] hover:text-white">
          <ArrowLeft size={22} />
        </Link>

        <div className="mt-8 flex flex-col items-center">
          <PeepingLogo className="w-44" />
        </div>

        <div className="mt-10">
          <h1 className="text-2xl font-bold">Welcome Back</h1>
          <p className="mt-1 text-sm text-[#a7a0af]">Your world is waiting.</p>
        </div>

        <form onSubmit={submit} className="mt-6 flex flex-col gap-3" noValidate>
          <div className="relative">
            <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#a7a0af]" />
            <input
              data-testid="login-email"
              type="text"
              inputMode="email"
              name="email"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address or username"
              className="field w-full rounded-2xl py-4 pl-11 pr-4 text-sm"
            />
          </div>
          <div className="relative">
            <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#a7a0af]" />
            <input
              data-testid="login-password"
              type={show ? "text" : "password"}
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="field w-full rounded-2xl py-4 pl-11 pr-11 text-sm"
            />
            <button
              type="button"
              onClick={() => setShow(!show)}
              data-testid="login-toggle-password"
              className="absolute right-4 top-1/2 -translate-y-1/2 text-[#a7a0af]"
              aria-label="Toggle password visibility"
            >
              {show ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          <div className="flex justify-end">
            <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs font-medium text-[#d42be8]">
              Forgot password?
            </Link>
          </div>

          {error && (
            <p data-testid="login-error" className="rounded-xl bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-300">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="btn-brand mt-2 w-full rounded-2xl py-4 text-base font-semibold text-white"
          >
            {loading ? "Signing in…" : "Log In"}
          </button>
        </form>

        <p className="mt-8 text-center text-sm text-[#a7a0af]">
          Don't have an account?{" "}
          <Link to="/register" data-testid="login-register-link" className="font-semibold text-[#d42be8]">
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
}
